import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ResultsActions } from './results.actions';
import { ResultsApiMapper } from './results-api.mapper';
import {
    ProjectsControllerService,
    SystemComparisonControllerService,
    VariantGetByIdV2,
    VariantsControllerService
} from '@nx-customer-apps/api-planning-projects-v2';
import { GlobalSpinnerService } from '@nx-customer-apps/components';

@Injectable({ providedIn: 'root' })
export class ResultsEffects {
    private actions$ = inject(Actions);
    private router = inject(Router);
    private resultsApiMapper = inject(ResultsApiMapper);
    private globalSpinnerService = inject(GlobalSpinnerService);
    private projectsControllerService = inject(ProjectsControllerService);
    private variantsControllerService = inject(VariantsControllerService);
    private systemComparisonControllerService = inject(SystemComparisonControllerService);

    public resetVariantsAndCalculateOptimalSystem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResultsActions.resetVariantsAndCalculateOptimalSystem),
            tap(() => this.globalSpinnerService.next({ isShown: true, loadingText: 'LOADER.TEXT.OPTIMAL_SYSTEM' })),
            switchMap(payload =>
                this.projectsControllerService
                    .projectsControllerGetProject({ id: payload.projectHexId, withVariants: true, applyCommercialPricing: true })
                    .pipe(
                        switchMap(project => this.deleteExistingVariants(project.variants)),
                        switchMap(() => this.projectsControllerService.projectsControllerRecalculateProject({ id: payload.projectHexId })),
                        map(() => ResultsActions.calculateOptimalSystem(payload)),
                        catchError(error => of(ResultsActions.calculateSystemFailure(error)))
                    )
            )
        )
    );

    public calculateOptimalSystem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResultsActions.calculateOptimalSystem),
            tap(() => this.globalSpinnerService.next({ isShown: true, loadingText: 'LOADER.TEXT.OPTIMAL_SYSTEM' })),
            switchMap(payload => {
                const body = this.resultsApiMapper.mapToOptimalSystemRequestDTO(payload);

                return this.variantsControllerService.variantsControllerCreateOptimalVariant({ body }).pipe(
                    map(response => {
                        const modelResponse = this.resultsApiMapper.mapToOptimalSystemResponseModel(response);

                        return ResultsActions.calculateOptimalSystemSuccess(modelResponse);
                    }),
                    catchError(error => of(ResultsActions.calculateSystemFailure(error)))
                );
            })
        )
    );

    public calculateSystemComparison$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResultsActions.calculateOptimalSystemSuccess),
            tap(() => this.globalSpinnerService.next({ isShown: true, loadingText: 'LOADER.TEXT.SYSTEM_COMPARISON' })),
            switchMap(payload => {
                const body = this.resultsApiMapper.mapToSystemComparisonRequestDTO(payload);

                return this.systemComparisonControllerService
                    .systemComparisonControllerPostSystemComparison({ id: payload.projectId!, applyCommercialPricing: true, body })
                    .pipe(
                        map(response => {
                            const modelResponse = this.resultsApiMapper.mapToSystemComparisonResponseModel(response);

                            return ResultsActions.calculateSystemComparisonSuccess(modelResponse);
                        }),
                        catchError(error => of(ResultsActions.calculateSystemFailure(error)))
                    );
            })
        )
    );

    public calculateSystemComparisonSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ResultsActions.calculateSystemComparisonSuccess),
                tap(() => {
                    this.globalSpinnerService.reset();
                    this.router.navigate(['/results']);
                })
            ),
        { dispatch: false }
    );

    public calculateSystemFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ResultsActions.calculateSystemFailure),
                tap(error => {
                    console.error('[RESULTS] calculateSystem failed', error);
                    this.globalSpinnerService.reset();
                })
            ),
        { dispatch: false }
    );

    private deleteExistingVariants(variants: VariantGetByIdV2[] | undefined): Observable<unknown> {
        if (!variants?.length) {
            return of(null);
        }

        return forkJoin(variants.map(variant => this.variantsControllerService.variantsControllerDeleteVariant({ id: variant.id })));
    }
}
